import {
  Stack,
  Text,
  Group,
  ScrollArea,
  Box,
  Button,
  UnstyledButton,
  ActionIcon,
  Alert,
  Tooltip,
} from '@mantine/core';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Info,
  X,
} from 'react-feather';
import { ContentCard } from '../../../../components/common/ContentCard';
import type {
  AuthoredAgendaSectionForCoach,
  CoachSessionLeaderNote,
} from '../../../../services/api/coachSessionPrep';
import type { ActiveSessionAiPromptSelection } from '../../../../services/api/coachActiveSession';
import type { ReadTheRoomAck } from '../../../../hooks/useSessionNudges';
import { RichTextContent } from '../../../../components/common/RichTextEditor';

/**
 * MED-792 AC1 + AC4 — left column "Session Guide" panel.
 * MED-976 (E1.3h) — sourced from the authored "sections" model.
 * MED-1013 — leader-only guide: an ordered stack of individually-collapsible
 * typed cards (Leader Prompt → Leader Note → AI In-Use prompt), each with a
 * color/header indicating its type and a "(N of M)" counter when a type has
 * more than one item. The participant prompt and slide chips are removed (they
 * live on the Whiteboard mirror, MED-1016). The prep-selected "In Use" AI
 * prompts render at the bottom when the payload carries them (MED-1029); until
 * then they're simply absent.
 * MED-1166 — the on-demand button (ex "Generate More Prompts", MED-872) is now
 * "Generate Recommendation": it reads the LIVE conversation via
 * session-intelligence (noticing line + read-aloud prompt), replaces rather
 * than stacks results, and shows a calm empty state when there is no confident
 * read — never the prep-time prompts.
 */

export interface ActiveSessionGuidePanelProps {
  section: AuthoredAgendaSectionForCoach | null;
  sectionIndex: number | null;
  /** Leader notes for the current session × group (filtered client-side by block_key). */
  leaderNotes: CoachSessionLeaderNote[];
  /** MED-872: AI gate (ai_features_enabled && ai_listening_enabled). */
  aiActive?: boolean;
  sessionId?: string;
  /** Total section count, for the "Step N of M" header (MED-1013). */
  sectionCount?: number;
  /**
   * MED-1013 / MED-1029 — prep-selected "In Use" AI prompts to render as the
   * bottom purple cards. Absent until MED-1029 surfaces them (AC5).
   */
  aiPromptSelections?: ActiveSessionAiPromptSelection[];
  /**
   * MED-1166 — requests an on-demand "read the room" from session-intelligence.
   * Passed ONLY for session leaders (the server refuses non-leaders, so the
   * page withholds it rather than render a dead button); absent → the
   * Generate Recommendation button is hidden entirely.
   */
  onGenerateRecommendation?: () => Promise<ReadTheRoomAck>;
  /**
   * MED-1166 — live AI-listening pause state. The empty-state copy describes a
   * moment ("paused" / "flowing"); when the leader pauses or resumes afterward
   * that claim goes stale and would contradict the header's listening badge,
   * so the panel clears it on any change.
   */
  aiPaused?: boolean;
}

// MED-1120 (E1.3p) — leader notes are keyed by the stable section id (not the
// old positional `${sectionIndex}:` prefix), so they follow their section
// across CM content edits/reorders. Match by section id.
function notesForSection(
  notes: CoachSessionLeaderNote[],
  sectionId: string | null
): CoachSessionLeaderNote[] {
  if (!sectionId) return [];
  return notes.filter(n => n.block_key === sectionId);
}

// MED-1013 — per-type card treatments (Figma node 2211:89486).
const GUIDE_CARD_STYLES = {
  prompt: {
    bg: '#FAFBFC',
    border: '#E5E7EB',
    label: '#6B7280',
    labelSize: 11,
    radius: 10,
  },
  note: {
    bg: '#FFFDF5',
    border: '#D97706',
    label: '#D97706',
    labelSize: 9,
    radius: 8,
  },
  ai: {
    bg: '#F8FAFB',
    border: '#8B5CB8',
    label: '#8B5CB8',
    labelSize: 9,
    radius: 8,
  },
} as const;

/**
 * MED-1013 — an individually-collapsible typed card in the Session Guide.
 * Color + header convey the type (Leader Prompt / Leader Note / AI). Defaults
 * to expanded; collapse state is per-session and not persisted (AC4).
 */
function GuideCard({
  variant,
  label,
  inUse = false,
  testid,
  children,
}: {
  variant: 'prompt' | 'note' | 'ai';
  label: string;
  inUse?: boolean;
  testid?: string;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const s = GUIDE_CARD_STYLES[variant];
  return (
    <Box
      data-testid={testid}
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: s.radius,
        padding: '13px 15px',
      }}
    >
      <UnstyledButton
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
        style={{ display: 'block', width: '100%' }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
          <Text
            fz={s.labelSize}
            fw={variant === 'prompt' ? 700 : 600}
            c={s.label}
            tt="uppercase"
            style={{ letterSpacing: '0.45px' }}
          >
            {label}
          </Text>
          <Group gap={6} align="center" wrap="nowrap">
            {inUse && (
              <Box
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: '#8B5CB8',
                  color: '#FFFFFF',
                  borderRadius: 4,
                  padding: '2px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <CheckCircle size={12} aria-hidden="true" />
                In Use
              </Box>
            )}
            {collapsed ? (
              <ChevronRight size={14} color="#9CA3AF" aria-hidden="true" />
            ) : (
              <ChevronDown size={14} color="#9CA3AF" aria-hidden="true" />
            )}
          </Group>
        </Group>
      </UnstyledButton>
      {!collapsed && <Box mt={10}>{children}</Box>}
    </Box>
  );
}

export function ActiveSessionGuidePanel({
  section,
  sectionIndex,
  leaderNotes,
  aiActive = false,
  sessionId,
  sectionCount,
  aiPromptSelections = [],
  onGenerateRecommendation,
  aiPaused = false,
}: ActiveSessionGuidePanelProps) {
  const sectionNotes = notesForSection(leaderNotes, section?.id ?? null);
  // MED-976 (E1.3h) AC-CL3 — facilitation prompts come from the authored
  // Leader Prompts list, sorted by sort_order to match the authoring order.
  const leaderPrompts = (section?.leader_prompts ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const sectionTitle = section?.section_name ?? null;

  // MED-1209 (D15) — the on-demand "read the room" results, as a navigable
  // in-session history (reverses MED-1166 AC6's replace-not-stack per the
  // 2026-07-08 UAT ask). Client-side only: cleared by reload/session end,
  // never persisted (IV5). The newest read is always shown by default (AC3),
  // so older items sit behind the back-arrow, never in the leader's way.
  // `items` and the `index` cursor live in ONE state object so every
  // transition (append/dismiss/navigate) is a single pure updater — the
  // cursor can never observe a list it wasn't derived from.
  const [recHistory, setRecHistory] = useState<{
    items: Array<{
      noticing: string;
      prompt: string;
      /** AC9 — where this read came from, so older items read as prior context. */
      sectionIndex: number | null;
      sectionTitle: string | null;
    }>;
    index: number;
  }>({ items: [], index: 0 });
  // AC3 — the calm empty state; 'paused' and 'quiet' get their own honest
  // wording. 'covered' (v3, ack reason nothing_new) = every live thread has
  // already been pulled — honest exhaustion, distinct from "flowing".
  const [emptyReason, setEmptyReason] = useState<
    null | 'flowing' | 'quiet' | 'paused' | 'covered'
  >(null);
  // MED-1179 AC4 — a fresh no-read result must restart the auto-dismiss timer
  // even when it repeats the SAME reason (e.g. 'flowing' twice in a row). React
  // bails out of re-firing effects when a state setter is passed an identical
  // value, so keying the timer on emptyReason alone would not reset for a
  // same-reason repeat. This nonce bumps on every no-read ack so the timer
  // effect below always sees a changed dependency and restarts the countdown.
  const [emptyReasonNonce, setEmptyReasonNonce] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  // Invalidates in-flight generations when their context is gone (section
  // change): an ack that settles after the reset must not repopulate the
  // just-cleared state with a read of the previous section's moment.
  const generationRef = useRef(0);

  // MED-1209 (D15) AC6 — the recommendation history SURVIVES section changes
  // (Ronnie 2026-07-15: one running list for the session, reversing
  // MED-1166's section-change clear). The generationRef bump stays: an ack
  // that settles after a section change must not paint the PREVIOUS
  // section's moment as a fresh read (the in-flight guard is about request
  // staleness, not the saved list). The momentary empty/error states still
  // clear — they describe a moment that has passed.
  useEffect(() => {
    generationRef.current += 1;
    setEmptyReason(null);
    setAiUnavailable(false);
  }, [sectionIndex]);

  // The paused/flowing empty state describes a moment; once the leader pauses
  // or resumes listening it goes stale and would contradict the live
  // listening badge in the header — clear it on any pause-state change.
  useEffect(() => {
    setEmptyReason(null);
  }, [aiPaused]);

  // MED-1179 AC1/AC4/IV2 — the calm empty state is a momentary answer, not a
  // standing state: auto-dismiss it after ~5s like a toast (AC1). The effect is
  // keyed on emptyReason AND emptyReasonNonce so every fresh no-read result —
  // including a same-reason repeat — restarts the countdown, giving the leader
  // the full read-time on the message currently on screen (AC4). The cleanup
  // clears any pending timer whenever the message is replaced, manually
  // dismissed, or nulled by the section/pause effects above, so a stale timer
  // never fires against a panel that has already moved on (IV2). Scoped to the
  // empty state only — the recommendation card and error alert are untouched
  // (AC5/IV3).
  useEffect(() => {
    if (!emptyReason) return;
    const timeout = setTimeout(() => setEmptyReason(null), 5000);
    return () => clearTimeout(timeout);
  }, [emptyReason, emptyReasonNonce]);

  const handleGenerateRecommendation = async () => {
    if (!onGenerateRecommendation) return;
    const requestGeneration = generationRef.current;
    setGenerating(true);
    setAiUnavailable(false);
    try {
      const ack = await onGenerateRecommendation();
      // Superseded while in flight (leader advanced sections) — the reset
      // already cleared this surface; don't paint a stale read onto it.
      if (requestGeneration !== generationRef.current) return;
      if (ack.status === 'ok') {
        // D15 AC1/AC3 — append to the history and land on the newest read;
        // navigation is how the leader gets BACK (AC2), generation always
        // brings them current. Section captured for the AC9 "from earlier"
        // marker (the in-flight guard above guarantees it's the section the
        // read was actually generated in).
        setRecHistory(prev => ({
          items: [
            ...prev.items,
            {
              noticing: ack.recommendation.noticing,
              prompt: ack.recommendation.suggested_prompt,
              sectionIndex,
              sectionTitle,
            },
          ],
          index: prev.items.length,
        }));
        setEmptyReason(null);
      } else if (ack.status === 'no_read') {
        // D15 AC5 — an empty read answers a MOMENT ("nothing new right now"),
        // it is not a history entry and it must not wipe the history the
        // leader may be reviewing. The alert renders alongside; the cursor
        // stays on the item they were viewing.
        // Unknown/future reasons deliberately fall through to 'flowing' — the
        // generic copy is honest for any no-read the client doesn't recognize.
        setEmptyReason(
          ack.reason === 'paused'
            ? 'paused'
            : ack.reason === 'insufficient_transcript'
              ? 'quiet'
              : ack.reason === 'nothing_new'
                ? 'covered'
                : 'flowing'
        );
        // MED-1179 AC4 — restart the ~5s auto-dismiss countdown for this fresh
        // result even if it repeats the reason currently on screen.
        setEmptyReasonNonce(n => n + 1);
      } else {
        // AC4 — transient error. Keep a previous recommendation on screen (an
        // error is not a new read), but clear a previous empty state — "the
        // conversation's flowing" stacked above an error contradicts itself.
        setEmptyReason(null);
        setAiUnavailable(true);
      }
    } catch {
      // onGenerateRecommendation resolves rather than throwing by contract;
      // last-resort guard so the spinner always settles (AC4).
      if (requestGeneration === generationRef.current) {
        setEmptyReason(null);
        setAiUnavailable(true);
      }
    } finally {
      setGenerating(false);
    }
  };

  // MED-1166 — the button renders only for leaders (the page withholds the
  // callback otherwise), inside the same AI gate as every AI surface (IV2).
  const canGenerate = aiActive && !!onGenerateRecommendation;

  // D15 AC4 — dismiss the read under the cursor; the card shows an adjacent
  // remaining item and the counter updates. Same affordance as the Insights
  // panel's Dismiss (MED-1167) so the two AI surfaces stay consistent. One
  // pure updater derives the clamped cursor from the same list it trims.
  const handleDismissRecommendation = () => {
    setRecHistory(prev => {
      const items = prev.items.filter((_, i) => i !== prev.index);
      return {
        items,
        index: Math.max(0, Math.min(prev.index, items.length - 1)),
      };
    });
  };

  const goToRec = (delta: number) =>
    setRecHistory(prev => ({
      ...prev,
      index: Math.max(0, Math.min(prev.items.length - 1, prev.index + delta)),
    }));

  const recommendations = recHistory.items;
  const activeRecIndex = recHistory.index;
  const activeRecommendation = recommendations[activeRecIndex] ?? null;

  // MED-1279 (Group B) — "jump to the next Leader Prompt". Purely additive: it
  // reads the DOM positions of the existing prompt cards and scrolls the panel;
  // it changes no card, no key, and no collapse state (AC17/AC18/AC20/AC21).
  // `viewportRef` is the ScrollArea's scrolling element (scroll math + scrollTo);
  // `contentRef` is the card Stack, observed for height changes (collapse/expand,
  // a recommendation card appearing) so the control's visibility keeps up.
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The target prompt's index within `leaderPrompts`, or null when there is no
  // next prompt to reach (single-prompt step, or already on the last prompt —
  // AC8/AC9). Derived from live scroll position, never persisted; it resets
  // with the step because the effect re-runs on section change (AC21).
  const [nextPromptIndex, setNextPromptIndex] = useState<number | null>(null);
  // AC1/AC2 — is there more content than the panel can show? We measure this
  // ourselves and drive the scrollbar off it (see the ScrollArea `type` below)
  // rather than trusting Mantine/Radix's built-in `type="auto"` overflow
  // detection, which does not fire reliably in every browser engine (it stayed
  // unmounted under overflow in WKWebView). Own-measured overflow makes the
  // "scrollbar shown exactly when it means something" guarantee deterministic.
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);

  const recomputeGuideAffordances = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      setIsContentOverflowing(false);
      setNextPromptIndex(null);
      return;
    }
    // AC1/AC2 — the scrollbar's presence tracks real overflow (1px tolerance
    // guards sub-pixel rounding). Measured for every step regardless of prompt
    // count, since a single long prompt must still show the scrollbar.
    setIsContentOverflowing(viewport.scrollHeight - viewport.clientHeight > 1);
    // AC8 — a single-prompt step never shows the jump control, regardless of
    // overflow (the scrollbar above already covers "this prompt continues").
    if (leaderPrompts.length < 2) {
      setNextPromptIndex(null);
      return;
    }
    const vpRect = viewport.getBoundingClientRect();
    const cards = viewport.querySelectorAll<HTMLElement>(
      '[data-testid="active-session-guide-leader-prompt"]'
    );
    // AC7/AC8 — "next" is the first prompt that is BOTH below the top of the
    // visible area AND not already fully visible (its card extends past the
    // bottom). Two conditions, not one:
    //  • below-the-top (AC7) gives the sequential 1 → 2 → 3 walk and keeps the
    //    card pinned at the top from counting as its own "next".
    //  • not-fully-visible (AC8) is what "a next prompt to reach" means — a
    //    prompt you can already read in full is not worth a jump. This hides the
    //    control when the panel overflows only because of the notes/AI cards
    //    below while every prompt is already in view, and it also handles the
    //    tail case where the scroll has maxed out and the short trailing prompts
    //    are all visible at once (bringing one to the very top is impossible, so
    //    there is nothing left to reach).
    const THRESHOLD_PX = 8;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i].getBoundingClientRect();
      const headerBelowTop = r.top > vpRect.top + THRESHOLD_PX;
      const cardNotFullyVisible = r.bottom > vpRect.bottom + THRESHOLD_PX;
      if (headerBelowTop && cardNotFullyVisible) {
        setNextPromptIndex(i);
        return;
      }
    }
    // AC8/AC9 — nothing below the top still needs reaching (leader is on/past the
    // last prompt, or every remaining prompt is fully in view): hide the control.
    // It never wraps back to the first prompt.
    setNextPromptIndex(null);
  }, [leaderPrompts.length]);

  // Keep the control's visibility in sync with scroll position and layout. The
  // ResizeObserver covers height changes the scroll listener can't see: a card
  // collapsing/expanding, the recommendation card appearing, or the viewport
  // resizing. Re-runs on section change so the "current prompt" resets per step
  // (AC21). The panel itself is never remounted — only this observer re-binds.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    recomputeGuideAffordances();
    viewport.addEventListener('scroll', recomputeGuideAffordances, {
      passive: true,
    });
    const observer = new ResizeObserver(recomputeGuideAffordances);
    observer.observe(viewport);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      viewport.removeEventListener('scroll', recomputeGuideAffordances);
      observer.disconnect();
    };
  }, [recomputeGuideAffordances, section?.id]);

  const handleJumpToNextPrompt = () => {
    const viewport = viewportRef.current;
    if (!viewport || nextPromptIndex === null) return;
    const cards = viewport.querySelectorAll<HTMLElement>(
      '[data-testid="active-session-guide-leader-prompt"]'
    );
    const target = cards[nextPromptIndex];
    if (!target) return;
    // AC6 — the ABSOLUTE scroll position that puts the target card's header at
    // the top of the visible area (or as close as the remaining scroll allows).
    // Measuring against the live rects absorbs the Stack gap and offsetScrollbars
    // padding without hardcoding them. Computed up front, before the focus below
    // can move the viewport.
    const delta =
      target.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
    const targetScrollTop = viewport.scrollTop + delta;
    // AC6 — scroll the target to the top. Instant (synchronous) rather than
    // smooth on purpose: it makes the header fully visible *before* the focus
    // below runs. Safari/WKWebView ignores `preventScroll` and forces a
    // focus-into-view that bottom-aligns any not-fully-visible target — and it
    // fires deferred, so it wins even if we scroll after focusing. Snapping the
    // scroll first means focus-into-view finds the header already in view and
    // becomes a no-op, so the header lands (and stays) at the top on every
    // engine. A whole-prompt jump also reads more clearly as a snap than a glide.
    viewport.scrollTo({ top: targetScrollTop });
    // AC15 — move the reading position to the target's header so a keyboard or
    // screen-reader user follows the jump instead of being left on the button.
    // Focus the card's existing header toggle (the "(N OF M)" row): focusing is
    // not clicking, so a collapsed target stays collapsed (AC18).
    const header = target.querySelector<HTMLElement>('button');
    header?.focus({ preventScroll: true });
  };

  const hasContent =
    leaderPrompts.length > 0 ||
    sectionNotes.length > 0 ||
    aiPromptSelections.length > 0 ||
    recommendations.length > 0;

  return (
    <ContentCard
      data-testid="active-session-guide-panel"
      style={{ backgroundColor: '#FAFAFA', borderRadius: 12 }}
      header={
        // MED-1279 (Group B) — the "Next prompt" jump control lives in the panel
        // HEADER, not floating over the panel's bottom edge. The original design
        // floated it (position:absolute / bottom:0) over the ScrollArea, but the
        // in-session page has TWO stacked scroll contexts: the panel's own card
        // scroller AND the page itself (a tall center column — the Commitment box,
        // documents — makes the grid row taller than the viewport, so the page
        // scrolls too). A control anchored to the panel's bottom then sits below
        // the fold until the leader page-scrolls, which is the whole thing this
        // feature exists to prevent (MED-1279 QA, Ben/Michelle 2026-07-30). The
        // header is always on screen on load with no positioning logic, so the
        // control is guaranteed visible. It shows only when there is a next prompt
        // to reach (nextPromptIndex), carrying AC8/AC9 unchanged, and its aria
        // name still conveys the destination (AC12/AC13/AC14).
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          gap={8}
          style={{ width: '100%' }}
        >
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text
              fz={12}
              fw={600}
              tt="uppercase"
              style={{ letterSpacing: '0.72px', color: '#B8A070' }}
            >
              Session Guide — Leader Notes &amp; Prompts
            </Text>
            {section !== null && (
              <Text fz={12} c="#666">
                Step {(sectionIndex ?? 0) + 1}
                {sectionCount ? ` of ${sectionCount}` : ''}
                {sectionTitle ? ` — ${sectionTitle}` : ''}
              </Text>
            )}
          </Stack>
          {nextPromptIndex !== null && (
            <Button
              size="xs"
              radius="xl"
              color="#2D4739"
              onClick={handleJumpToNextPrompt}
              rightSection={<ChevronDown size={14} aria-hidden="true" />}
              aria-label={`Go to Leader Prompt ${nextPromptIndex + 1} of ${leaderPrompts.length}`}
              data-testid="active-session-guide-next-prompt"
              style={{ flexShrink: 0 }}
            >
              Next prompt
            </Button>
          )}
        </Group>
      }
    >
      <Box
        style={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ScrollArea
          viewportRef={viewportRef}
          // AC1/AC2 — show the scrollbar exactly when content overflows, driven
          // by our own measurement (isContentOverflowing) instead of Radix's
          // built-in `type="auto"` detection, which did not fire in WKWebView.
          // `always` renders unconditionally when overflowing; `never` hides it
          // when everything fits. offsetScrollbars keeps the reserved gutter in
          // both states so the content width never shifts (AC4).
          type={isContentOverflowing ? 'always' : 'never'}
          offsetScrollbars
          style={{ flexGrow: 1 }}
        >
          {section === null ? (
            <Text size="sm" c="dimmed">
              No section is currently active.
            </Text>
          ) : (
            <Stack gap={12} ref={contentRef}>
              {/* AC2 order — Leader Prompt(s) first (Medley's base content). */}
              {/* MED-1270 — compose the stable section.id into the key. Keying on
                the position alone (`prompt-${idx}`) let React reconcile a step
                change as the SAME element and preserve each GuideCard's local
                `collapsed` state onto the next step's prompt (the collapse
                leaked onto the slot, not the content). Section identity in the
                key makes a step change a fresh instance that honors the
                expanded default (MED-1013 AC4). `leader_prompts` has no
                per-prompt id, so idx stays as the within-section discriminator;
                section.id is stable across CM edits/reorders (why MED-1120 keys
                notes on it), so a mid-session content edit won't re-expand the
                cards in use. */}
              {leaderPrompts.map((prompt, idx) => (
                <GuideCard
                  key={`prompt-${section.id}-${idx}`}
                  variant="prompt"
                  label={
                    leaderPrompts.length > 1
                      ? `Leader Prompt (${idx + 1} of ${leaderPrompts.length})`
                      : 'Leader Prompt'
                  }
                  testid="active-session-guide-leader-prompt"
                >
                  {prompt.intro_text && prompt.intro_text.trim().length > 0 && (
                    <Text size="xs" c="dimmed" fw={600} mb={4}>
                      {prompt.intro_text.trim()}
                    </Text>
                  )}
                  <RichTextContent
                    content={prompt.discussion_prompt}
                    emptyText="(no prompt body)"
                  />
                </GuideCard>
              ))}

              {/* Leader Note(s) — the leader's own context. */}
              {sectionNotes.map((note, idx) => (
                <GuideCard
                  key={note.id}
                  variant="note"
                  label={
                    sectionNotes.length > 1
                      ? `Leader Note (${idx + 1} of ${sectionNotes.length})`
                      : 'Leader Notes'
                  }
                  testid="active-session-guide-leader-notes"
                >
                  <RichTextContent content={note.content_html} />
                </GuideCard>
              ))}

              {/* AC5 — prep-selected "In Use" AI prompts (purple), at the bottom.
                Sourced from the payload (MED-1029); absent until then. */}
              {/* MED-1270 — same fix as the prompt cards. `segment` is a fixed
                vocabulary (purpose / opening_prompt / stall_recovery), so the
                key repeated across steps and leaked the collapsed state the
                same way (latent until two consecutive steps carry an In Use
                card for the same segment). Compose section.id so a step change
                yields a fresh, expanded instance. */}
              {aiPromptSelections.map(sel => (
                <GuideCard
                  key={`ai-${section.id}-${sel.segment}`}
                  variant="ai"
                  label={sel.label}
                  inUse
                  testid="active-session-guide-ai-inuse"
                >
                  <Text size="sm">{sel.body}</Text>
                </GuideCard>
              ))}

              {/* MED-1166 AC2 card, now history-aware (MED-1209 / D15): the same
                in-place card reviews a navigable set — prev/next arrows + the
                MED-1013 "(N of M)" counter idiom (AC2), Insights-style Dismiss
                (AC4), and a "from earlier" marker when the read came from a
                previous agenda section (AC9). */}
              {canGenerate && activeRecommendation && (
                <Box
                  data-testid="active-session-guide-recommendation"
                  style={{
                    background: '#F8FAFB',
                    border: '1px solid #8B5CB8',
                    borderRadius: 8,
                    padding: '13px 15px',
                  }}
                >
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap={6} align="center" wrap="nowrap">
                      <span aria-hidden="true" style={{ fontSize: 12 }}>
                        ✨
                      </span>
                      <Text
                        fz={10}
                        fw={700}
                        c="#8B5CB8"
                        tt="uppercase"
                        style={{ letterSpacing: '0.45px' }}
                      >
                        What I’m noticing
                        {recommendations.length > 1
                          ? ` (${activeRecIndex + 1} of ${recommendations.length})`
                          : ''}
                      </Text>
                    </Group>
                    <Group gap={4} align="center" wrap="nowrap">
                      {recommendations.length > 1 && (
                        <>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            aria-label="Previous recommendation"
                            data-testid="active-session-guide-recommendation-prev"
                            disabled={activeRecIndex === 0}
                            onClick={() => goToRec(-1)}
                          >
                            <ChevronLeft size={14} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            aria-label="Next recommendation"
                            data-testid="active-session-guide-recommendation-next"
                            disabled={
                              activeRecIndex >= recommendations.length - 1
                            }
                            onClick={() => goToRec(1)}
                          >
                            <ChevronRight size={14} />
                          </ActionIcon>
                        </>
                      )}
                      {/* Same treatment as the Insights Dismiss (MED-1167) —
                        colors duplicated on purpose: factoring them out of the
                        shipped panel would violate IV1's no-refactor guardrail. */}
                      <Tooltip label="Remove from your list" withArrow>
                        <ActionIcon
                          variant="default"
                          size="sm"
                          aria-label="Dismiss"
                          data-testid="active-session-guide-recommendation-dismiss"
                          onClick={handleDismissRecommendation}
                          styles={{
                            root: {
                              background: '#FCF1F0',
                              borderColor: '#E3B7B3',
                              color: '#B4443C',
                            },
                          }}
                        >
                          <X size={12} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  {activeRecommendation.sectionIndex !== null &&
                    sectionIndex !== null &&
                    activeRecommendation.sectionIndex !== sectionIndex && (
                      <Text
                        fz={11}
                        c="#8a7cb8"
                        fs="italic"
                        mt={4}
                        data-testid="active-session-guide-recommendation-earlier"
                      >
                        From earlier — Step{' '}
                        {activeRecommendation.sectionIndex + 1}
                        {activeRecommendation.sectionTitle
                          ? ` · ${activeRecommendation.sectionTitle}`
                          : ''}
                      </Text>
                    )}
                  <Text size="sm" c="#374151" mt={6} lh={1.5}>
                    {activeRecommendation.noticing}
                  </Text>
                  <Box
                    mt={10}
                    data-testid="active-session-guide-recommendation-prompt"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #8B5CB8',
                      borderRadius: 8,
                      padding: '12px 13px',
                    }}
                  >
                    <Text
                      fz={10}
                      fw={700}
                      c="#8B5CB8"
                      tt="uppercase"
                      style={{ letterSpacing: '0.45px' }}
                    >
                      Suggested prompt
                    </Text>
                    <Text size="sm" fs="italic" c="#374151" mt={4} lh={1.5}>
                      “{activeRecommendation.prompt}”
                    </Text>
                  </Box>
                </Box>
              )}

              {!hasContent && !aiActive && (
                <Text size="sm" c="dimmed">
                  No prep notes or facilitation prompts captured for this
                  section.
                </Text>
              )}

              {/* MED-1166 AC3 — the calm, self-explaining empty state: never an
                error, never a "generation failed", never prep prompts. The
                paused variant is honest about WHY there is no read. */}
              {canGenerate && emptyReason && (
                <Alert
                  variant="light"
                  color="surveyPurple"
                  icon={<Info size={14} />}
                  // MED-1179 AC2 — a visible dismiss (X) so the leader can clear
                  // the momentary "nothing to flag" read immediately without
                  // waiting for the ~5s timer. onClose only nulls emptyReason, so
                  // dismissing never re-triggers a generation (AC3).
                  withCloseButton
                  closeButtonLabel="Dismiss"
                  onClose={() => setEmptyReason(null)}
                  data-testid="active-session-guide-recommendation-empty"
                  styles={{ message: { width: '100%' } }}
                >
                  <Text size="xs">
                    {emptyReason === 'paused'
                      ? 'AI listening is paused for this session, so I don’t have a live read of the room. Resume listening to get an in-the-moment recommendation.'
                      : emptyReason === 'quiet'
                        ? 'There hasn’t been enough conversation recently for me to get a read of the room. I’m listening for moments where the group stalls, one voice dominates, or someone goes quiet — tap again once the discussion gets going.'
                        : emptyReason === 'covered'
                          ? // v3 honest exhaustion — distinct from "flowing": the
                            // threads in play have been asked about (by you or by
                            // earlier prompts), and repeating them would be noise.
                            'You’ve covered the threads that are live right now — between your own questions and the earlier prompts, there’s nothing worth re-asking yet. Keep going; tap again once the conversation moves somewhere new.'
                          : // "new" so it reads honestly above a retained history
                            // card (D15 AC5/AC9) — the leader's prior reads still
                            // stand; this read just surfaced nothing to add.
                            'Nothing new to flag right now — the conversation’s flowing. I’m listening for moments where the group stalls, one voice dominates, or someone goes quiet. Tap again when you’d like me to try another read.'}
                  </Text>
                </Alert>
              )}

              {/* MED-1166 AC4 — a clearly worded, retryable transient error: the
                spinner always resolves here instead of hanging. Any result
                already on screen stays (an error is not a new read). */}
              {canGenerate && aiUnavailable && (
                <Alert
                  variant="light"
                  color="gray"
                  icon={<AlertTriangle size={14} />}
                  data-testid="active-session-guide-ai-unavailable"
                  styles={{ message: { width: '100%' } }}
                >
                  <Stack gap={6}>
                    <Text size="xs">
                      Couldn’t get a read of the room just now. The prompts
                      above remain in effect.
                    </Text>
                    <Button
                      variant="subtle"
                      color="surveyPurple"
                      size="compact-xs"
                      loading={generating}
                      onClick={handleGenerateRecommendation}
                      disabled={!sessionId || section === null}
                      data-testid="active-session-guide-ai-retry"
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Try again
                    </Button>
                  </Stack>
                </Alert>
              )}

              {/* MED-1166 AC5 — same placement and interaction as the old
                "Generate More Prompts" button; renamed because it no longer
                produces prep prompts, with the "read the room" intent in the
                tooltip. */}
              {canGenerate && (
                <Tooltip
                  label="Read the room: get an in-the-moment suggestion based on what's happening right now."
                  withArrow
                  multiline
                  maw={300}
                >
                  <Button
                    variant="default"
                    size="xs"
                    loading={generating}
                    onClick={handleGenerateRecommendation}
                    disabled={!sessionId || section === null}
                    leftSection={
                      <span aria-hidden="true" style={{ fontSize: 12 }}>
                        ✨
                      </span>
                    }
                    data-testid="active-session-guide-generate-recommendation"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Generate Recommendation
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
        </ScrollArea>
      </Box>
    </ContentCard>
  );
}
